import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, InputDescription, Pane, TextInput } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { MAX_DESCRIPTION_LENGTH } from '../../constants';
import { DescriptionMarkdownPreview } from '@fullstory/junk-drawer-ui';
import { DebouncedFunc, debounce } from 'lodash';

export interface TitleFormProps {
  itemId: string;
  title: string;
  placeholder: string;
  description?: string | null;
  onChange: (name: string, description?: string) => void;
  setSaveDisabled: (enabled: boolean) => void;
  disableMargin?: boolean;
  saveClicked: boolean;
}

interface FormErrors {
  title?: string;
}

const validateForm = (title: string): FormErrors => {
  const errors: FormErrors = {};
  if (!title) {
    errors.title = intl.formatMessage({
      defaultMessage: 'Title is required.',
      id: 'PZvrnY',
    });
  }
  return errors;
};

export const TitleForm: React.FC<TitleFormProps> = ({
  itemId,
  title,
  placeholder,
  description = null,
  onChange,
  setSaveDisabled,
  disableMargin = false,
  saveClicked,
}) => {
  const [pendingTitle, setPendingTitle] = useState(title);
  const [pendingDescription, setPendingDescription] = useState(
    description === null ? '' : description,
  );
  const [titleTouched, setTitleTouched] = useState(false);

  const errors = useMemo(() => validateForm(pendingTitle), [pendingTitle]);

  useEffect(() => {
    setPendingTitle(title);
  }, [title]);

  useEffect(() => {
    setPendingDescription(description ?? '');
  }, [description]);

  const debounceRef = useRef<DebouncedFunc<(newTitle: any, desc: any) => void>>();
  const onChangeDebounce = useMemo(() => {
    if (debounceRef.current) {
      debounceRef.current.cancel();
    }

    const debounceFn = debounce((newTitle, desc) => {
      if (title !== newTitle) onChange(newTitle, desc);
    }, 500);
    debounceRef.current = debounceFn;

    return debounceFn;
  }, [onChange, title]);

  useEffect(
    () => () => {
      if (debounceRef.current) {
        debounceRef.current.cancel();
      }
    },
    [onChange],
  );

  const titleFormError = useMemo(
    () => !!errors.title && (saveClicked || titleTouched),
    [errors.title, saveClicked, titleTouched],
  );

  const titleElement = (
    <Box width="100%">
      <TextInput
        id={`${itemId}-title`}
        value={pendingTitle}
        placeholder={placeholder}
        onChange={e => {
          setPendingTitle(e.target.value);
          onChangeDebounce(e.target.value, pendingDescription);
        }}
        onBlur={e => {
          setPendingTitle(e.target.value);
          if (debounceRef.current) {
            debounceRef.current.cancel();
          }
          onChange(e.target.value, pendingDescription);
          if (!titleTouched) {
            setTitleTouched(true);
          }
        }}
        invalid={titleFormError}
        testId="title-form-title-input"
      />
      {titleFormError && <InputDescription invalid description={errors.title ?? ''} />}
    </Box>
  );

  const descriptionElement = (
    <Box width="100%">
      <DescriptionMarkdownPreview
        testId="title-form-description-input"
        id={`${itemId}-description`}
        onChange={e => {
          setPendingDescription(e.target.value);
        }}
        onBlur={e => {
          setPendingDescription(e.target.value);
          if (title !== e.target.value) onChange(pendingTitle, e.target.value);
        }}
        value={pendingDescription}
        placeholder={intl.formatMessage({
          defaultMessage: `Add a description... (optional)`,
          id: 'hcPqPd',
        })}
        maxLength={MAX_DESCRIPTION_LENGTH}
        preventNewline
        setSaveDisabled={disabled => setSaveDisabled(disabled || !!Object.keys(errors).length)}
      />
    </Box>
  );

  return (
    <Box data-component="TitleForm" testId="title-form" margin={disableMargin ? 0 : 24}>
      <Pane backgroundColor="default" border="standard" borderRadius={4}>
        <Box margin={24}>
          <Flex column gap={16} alignItems="flex-start">
            {titleElement}
            {descriptionElement}
          </Flex>
        </Box>
      </Pane>
    </Box>
  );
};
